import { Icon } from '@/components/Icon';
import { EmptyState, StatusBadge, TestModeChip } from '@/components/primitives';
import type { RunSummary } from '@/types/sturm';

const TRIGGER_LABEL: Record<string, string> = {
  schedule: 'Schedule',
  event: 'Event',
  api: 'API',
  manual: 'Manual',
  retry: 'Retry',
};

interface RunsTableProps {
  runs: RunSummary[];
  showWorkflow: boolean;
  onOpen: (run: RunSummary) => void;
  emptyTitle?: string;
  emptySub?: string;
}

export function RunsTable({ runs, showWorkflow, onOpen, emptyTitle, emptySub }: RunsTableProps) {
  if (runs.length === 0) {
    return (
      <div className="card">
        <EmptyState
          title={emptyTitle ?? 'No runs yet'}
          sub={emptySub ?? 'Runs appear here as soon as the workflow is triggered.'}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      <table className="tbl" style={{ minWidth: showWorkflow ? 1000 : 940 }}>
        <thead>
          <tr>
            <th>Run</th>
            {showWorkflow ? <th>Workflow</th> : null}
            <th>Status</th>
            <th>Current step</th>
            {showWorkflow ? null : <th>Trigger</th>}
            <th>Key</th>
            <th style={{ textAlign: 'right' }}>Attempts</th>
            <th style={{ textAlign: 'right' }}>Duration</th>
            <th style={{ textAlign: 'right' }}>Started</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id} className="clickable" onClick={() => onOpen(run)}>
              <td>
                <span className="row gap-6">
                  <span className="mono link-id">{run.runKey}</span>
                  {run.isTestMode ? <TestModeChip /> : null}
                </span>
              </td>
              {showWorkflow ? (
                <td>
                  <div className="wf-name" style={{ fontSize: 13 }}>
                    {run.workflowName}
                  </div>
                  <div className="wf-queue">{run.app}</div>
                </td>
              ) : null}
              <td>
                <StatusBadge status={run.status} />
              </td>
              <td>
                <span
                  className="mono tiny"
                  style={{ color: run.status === 'failed' ? 'var(--error)' : 'var(--ink-700)' }}
                >
                  {run.currentStep ?? '—'}
                </span>
              </td>
              {showWorkflow ? null : (
                <td>
                  <span className="tiny muted">{TRIGGER_LABEL[run.trigger] ?? run.trigger}</span>
                </td>
              )}
              <td>
                <span className="mono tiny muted">{run.businessKey ?? '—'}</span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className={`wf-num ${run.attempts > 1 ? 'bad' : 'dim'}`}>{run.attempts}</span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className="mono tiny">{run.duration}</span>
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className="tiny muted">{run.started}</span>
              </td>
              <td style={{ width: 30, color: 'var(--ink-300)' }}>
                <Icon name="chevron" size={13} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
